import { captureSentryMessage } from '@shared/sentry';
import { BehaviorSubject } from 'rxjs';
import { ONE_SECOND_MS } from '@shared/utils/constants';
import { Services } from './services.service';
import { BUFFERED_REQUEST_STORAGE_PREFIX } from '@shared/extension/constants';

const NETWORK_STATUS_CHECK_INTERVAL = ONE_SECOND_MS * 2;
// Minimum number of buffered requests to consider backend disconnected
const BACKEND_DISCONNECT_THRESHOLD = 10;

const OFFLINE_ERRORS = [
  'net::ERR_INTERNET_DISCONNECTED',
  'net::ERR_CONNECTION_REFUSED',
  'net::ERR_NAME_NOT_RESOLVED',
  'net::ERR_NETWORK_CHANGED',
  'net::ERR_TIMED_OUT',
];

export class NetworkService {
  readonly isOnline$ = new BehaviorSubject<boolean>(true);
  readonly isBackendConnected$ = new BehaviorSubject<boolean>(true);

  private ignoreOnLineStatusChange = false;
  private bufferedRequestsCount = 0;

  constructor(private services: Services) {
    this.initListeners();
    this.initStorageMonitoring();
  }

  private initListeners(): void {
    try {
      chrome.webRequest.onErrorOccurred.addListener(this.handleWebRequestErrorOccurred, { urls: ['<all_urls>'] });
      chrome.webRequest.onCompleted.addListener(this.handleWebRequestCompleted, { urls: ['<all_urls>'] });

      setInterval(() => {
        this.checkNetworkStatus();
      }, NETWORK_STATUS_CHECK_INTERVAL);

      this.isOnline$.subscribe(isOnline => {
        captureSentryMessage(`Network status changed: ${isOnline ? 'Online' : 'Offline'}, bufferedRequests: ${this.bufferedRequestsCount}`);
        if (isOnline) {
          this.ignoreOnLineStatusChange = false;
        }
      });
    } catch (e) {
      console.error('Error while initializing network service', e);
    }
  }

  private initStorageMonitoring(): void {
    chrome.storage.onChanged.addListener((changes, areaName) => {
      if (areaName !== 'local') return;

      const bufferedRequestDelta = Object.entries(changes)
        .filter(([key]) => key.startsWith(BUFFERED_REQUEST_STORAGE_PREFIX))
        .reduce(
          (delta, [_, change]) =>
            change.newValue && !change.oldValue ? delta + 1 : !change.newValue && change.oldValue ? delta - 1 : delta,
          0
        );

      if (bufferedRequestDelta !== 0) {
        this.bufferedRequestsCount += bufferedRequestDelta;
        this.updateBackendConnectivity();
      }
    });
  }

  private updateBackendConnectivity(): void {
    // Only consider backend disconnected if we have more than threshold buffered requests
    // TODO add health check endpoint to backend
    const isConnected = this.bufferedRequestsCount < BACKEND_DISCONNECT_THRESHOLD;

    if (this.isBackendConnected$.value !== isConnected) {
      this.isBackendConnected$.next(isConnected);
      captureSentryMessage(`Backend connectivity changed: ${isConnected ? 'Connected' : 'Disconnected'}`, {
        bufferedRequests: this.bufferedRequestsCount,
        threshold: BACKEND_DISCONNECT_THRESHOLD,
      });
    }
  }

  private handleWebRequestErrorOccurred = (details: chrome.webRequest.WebResponseErrorDetails): void => {
    try {
      if (
        OFFLINE_ERRORS.includes(details.error) &&
        details.url.includes(this.services.commonStore.backendUrl$.value) &&
        this.isOnline$.value
      ) {
        this.isOnline$.next(false);
        if (navigator.onLine) {
          this.ignoreOnLineStatusChange = true;
        }
      }
    } catch (e) {
      console.error('Error while handling webRequest errorOccurred', e);
    }
  };

  private handleWebRequestCompleted = (): void => {
    if (!this.isOnline$.value && navigator.onLine) {
      this.isOnline$.next(true);
    }
  };

  private checkNetworkStatus(): void {
    if (navigator.onLine) {
      if (!this.isOnline$.value && !this.ignoreOnLineStatusChange) {
        this.isOnline$.next(true);
      }
    } else {
      if (this.isOnline$.value) {
        this.isOnline$.next(false);
      }
    }
  }
}
